// Conversation ownership is server-authoritative. No POST runs during polling.
const clone = value => JSON.parse(JSON.stringify(value));
const same = (left, right) => JSON.stringify(left) === JSON.stringify(right);

export const ownershipScope = (accountId, conversationId) => {
  const account = Number(accountId);
  const conversation = Number(conversationId);
  return Number.isSafeInteger(account) && account > 0 &&
    Number.isSafeInteger(conversation) && conversation > 0
    ? { accountId: account, conversationId: conversation }
    : null;
};

export const eligibleOwnershipAgents = (agents, record, currentUserId) => {
  const ids = new Set(record?.assignable_agent_ids || []);
  return (agents || []).filter(agent =>
    Number.isSafeInteger(agent.id) && ids.has(agent.id) &&
    agent.id !== Number(currentUserId) &&
    (!agent.assignee_type || agent.assignee_type === 'User')
  );
};

export const ownershipUrl = scope =>
  `/api/v1/accounts/${scope.accountId}/conversations/${scope.conversationId}/miniapp_staff_ownership`;

export const ownershipSuccessKind = (action, record, currentUserId, targetId) => {
  if (action === 'return' && record?.state === 'alexey') return 'RETURNED';
  if (action === 'takeover' && record?.state === 'staff' && record.assignee_id === Number(currentUserId)) return 'TAKEN';
  if (action === 'assign' && record?.state === 'staff' && record.assignee_id === targetId) return 'ASSIGNED';
  return 'UPDATED';
};

const empty = () => ({ record: null, loading: false, busy: false, error: null, retryable: false });

export class OwnershipController {
  constructor({ api, onChange, onSuccess = () => {}, uuid = () => crypto.randomUUID() }) {
    this.api = api;
    this.onChange = onChange;
    this.onSuccess = onSuccess;
    this.uuid = uuid;
    this.generation = 0;
    this.sequence = 0;
    this.pending = null;
    this.scope = null;
    this.state = empty();
    this.disposed = false;
  }

  update(patch) {
    this.state = { ...this.state, ...patch };
    if (!this.disposed) this.onChange(this.state);
  }

  current(generation) {
    return !this.disposed && generation === this.generation;
  }

  setContext(accountId, conversationId) {
    this.generation += 1;
    this.sequence += 1;
    this.scope = ownershipScope(accountId, conversationId);
    this.pending = null;
    this.state = empty();
    this.onChange(this.state);
    return this.refresh();
  }

  async refresh({ preserveError = false } = {}) {
    if (!this.scope || this.disposed || this.state.busy) return;
    const scope = { ...this.scope };
    const generation = this.generation;
    const sequence = ++this.sequence;
    this.update({ loading: true, ...(preserveError || this.pending ? {} : { error: null }) });
    try {
      const record = await this.api.get(scope);
      if (!this.current(generation) || sequence !== this.sequence) return;
      // A changed generation cannot safely replay an uncertain earlier intent.
      if (this.pending && !same(this.pending.expected, record.snapshot)) {
        this.pending = null;
        this.update({ retryable: false });
      }
      this.update({ record, loading: false });
    } catch {
      if (this.current(generation) && sequence === this.sequence) {
        this.update({ loading: false, error: 'load' });
      }
    }
  }

  async act(actionType) {
    const record = this.state.record;
    if (!this.scope || this.disposed || this.state.busy || this.state.loading || this.pending || !record?.supported) return;
    if (actionType === 'takeover' ? !record.can_take_over : actionType !== 'return' || !record.can_return_to_alexey) return;
    this.pending = {
      action_type: actionType,
      client_request_id: this.uuid(),
      expected: clone(record.snapshot),
    };
    return this.postPending();
  }

  retry() {
    if (this.state.retryable && !this.state.busy && !this.state.loading && this.pending) return this.postPending();
    return undefined;
  }

  async postPending() {
    const generation = this.generation;
    const scope = { ...this.scope };
    const intent = this.pending;
    ++this.sequence; // An earlier GET must not overwrite the result of this POST.
    this.update({ busy: true, error: null, retryable: false });
    try {
      const record = await this.api.post(scope, clone(intent));
      if (!this.current(generation)) return;
      if (record?.supported !== true) throw new Error('Unexpected ownership response');
      this.pending = null;
      this.update({ record, busy: false, error: null });
      this.onSuccess(intent.action_type, scope, record);
    } catch (error) {
      if (!this.current(generation)) return;
      const status = error?.response?.status;
      if (!status || status >= 500) {
        // The server may have committed. Only the exact original UUID may retry.
        this.update({ busy: false, retryable: true, error: 'network' });
        return;
      }
      this.pending = null;
      this.update({ busy: false, error: status === 409 ? 'conflict' : 'denied' });
      await this.refresh({ preserveError: true });
    }
  }

  async assign(agentId, nativeAgents, currentUserId) {
    const record = this.state.record;
    if (!this.scope || this.disposed || this.state.busy || this.state.loading || this.pending || !record?.supported) return;
    if (!eligibleOwnershipAgents(nativeAgents, record, currentUserId).some(agent => agent.id === agentId)) return;
    const generation = this.generation;
    const scope = { ...this.scope };
    ++this.sequence;
    this.update({ busy: true, error: null });
    try {
      await this.api.assign(scope, agentId);
      if (!this.current(generation)) return;
      this.update({ busy: false });
      await this.refresh();
      if (!this.current(generation)) return;
      this.onSuccess('assign', scope, this.state.error ? null : this.state.record, agentId);
    } catch {
      if (!this.current(generation)) return;
      this.update({ busy: false, error: 'assignment' });
      await this.refresh({ preserveError: true });
    }
  }

  dispose() {
    this.disposed = true;
    this.generation += 1;
    this.pending = null;
  }
}
